import * as React from 'react'

import useFocus from '../../../Shared/hooks/useFocus'
import { informationIcon, safeParseHtml } from '../../../Shared/index'
import { ReadAloudContent } from '../../../Shared/components/readAloud/readAloudContent'
import { ReadAloudHtml } from '../../../Shared/components/readAloud/readAloudHtml'
import { ReadAloudSource } from '../../../Shared/components/readAloud/readAloudSource'
import { CLIP_SOURCE_TYPE, clipSource } from '../../../Shared/libs/speech/clipSource'
import { DEFAULT_HIGHLIGHT_PROMPT, } from '../../../Shared/utils/constants'

const DIRECTIONS_LABEL = 'Read the directions aloud'
const TASK_LABEL = 'Read the task aloud'
const ESSENTIAL_KNOWLEDGE_LABEL = 'Read the essential knowledge aloud'
const highlightDirections = [<li key='highlight-direction-1'>First, read the highlighting task below.</li>, <li key='highlight-direction-2'>Then, read the text and highlight sentences to complete the task.</li>, <li className="sr-only" key='highlight-direction-3'>Screenreader users, please use your tab key to navigate through the passage. Highlightable sentences are toggle-able buttons. Use the Enter key on a focused sentence to add it to your highlights or to remove it once it has already been highlighted.</li>]

export const taskPromptNode = (highlightPrompt?: string) => {
  const prompt = highlightPrompt || DEFAULT_HIGHLIGHT_PROMPT
  return /<[^>]+>/.test(prompt)
    ? <p>{safeParseHtml(prompt)}</p>
    : <p>{DEFAULT_HIGHLIGHT_PROMPT}{prompt.replace(DEFAULT_HIGHLIGHT_PROMPT, '')}</p>
}

const renderDirections = (directionElementsArray: JSX.Element[]) => (
  <section className="directions-section">
    <h2>Directions</h2>
    <ReadAloudContent label={DIRECTIONS_LABEL}>
      <ul>
        {directionElementsArray}
      </ul>
    </ReadAloudContent>
  </section>
)

const essentialKnowledgeSection = ({ essential_knowledge_text }) => {
  if(!essential_knowledge_text || essential_knowledge_text === '<br/>') { return }

  return (
    <div className="essential-knowledge-section">
      <div className="essential-knowledge-header">
        <img alt={informationIcon.alt} src={informationIcon.src} />
        <h3>Building Essential Knowledge</h3>
      </div>
      <ReadAloudHtml className="essential-knowledge" html={essential_knowledge_text} label={ESSENTIAL_KNOWLEDGE_LABEL} />
    </div>
  )
}

const DirectionsSection = ({ className, passage, inReflection, }) => {
  const [containerRef, setContainerFocus] = useFocus()

  React.useEffect(() => {
    setContainerFocus()
  }, [inReflection])

  if (inReflection) {
    const reflection = <p>Great! Now take a moment to reflect on the sentences you highlighted. The ideas you highlighted may be helpful as you complete the writing prompts in the next section.</p>
    return (
      <div className={`no-focus-outline ${className}`} ref={containerRef} tabIndex={-1}>
        <section className="reflection-section">
          <h2>Directions</h2>
          <ReadAloudContent label={DIRECTIONS_LABEL}>{reflection}</ReadAloudContent>
        </section>
      </div>
    )
  }

  const taskPrompt = taskPromptNode(passage.highlight_prompt)

  return (
    <ReadAloudSource source={clipSource(CLIP_SOURCE_TYPE.EVIDENCE_PASSAGE, passage.id)}>
      <div className={`no-focus-outline ${className}`} ref={containerRef} tabIndex={-1}>
        {renderDirections(highlightDirections)}
        <section className="task-section">
          <h3>Task</h3>
          <ReadAloudContent label={TASK_LABEL}>{taskPrompt}</ReadAloudContent>
        </section>
        {essentialKnowledgeSection(passage)}
      </div>
    </ReadAloudSource>
  )
}

export default DirectionsSection
